import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import {
  Badge,
  Box,
  Button,
  Container,
  Field,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import {
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiEdit2,
  FiPlus,
  FiRotateCcw,
  FiTrash2,
} from "react-icons/fi"

import {
  BudgetCategorys,
  BudgetCategoryTypes,
  ServiceCatalog,
  ServiceItemProviderRates,
  type BudgetCategoryPublic,
  type BudgetCategoryTypePublic,
  type BudgetSubCategoryPublic,
  type ServiceItemPublic as ServiceItem,
  type ServiceItemProviderRateAuditPublic as ServiceItemProviderRateAudit,
} from "@/client"
import useAuth from "@/hooks/useAuth"
import { handleError } from "@/utils"
import type { AxiosError as ApiError } from "axios"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

export const Route = createFileRoute("/_layout/provider/servicecatalog")({
  component: ServiceCatalogPage,
})

// ── Edit service item dialog ──────────────────────────────────────────────────

const EditItemDialog = ({
  item,
  trigger,
}: {
  item: ServiceItem
  trigger: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { register, handleSubmit } = useForm({ defaultValues: { name: item.name } })

  const mutation = useMutation({
    mutationFn: (data: { name: string }) =>
      ServiceCatalog.updateServiceItem({ path: { serviceItemId: item.id }, body: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceItems", item.budgetSubCategoryId] })
      setOpen(false)
    },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit Service Item</DialogTitle></DialogHeader>
        <DialogBody>
          <form id="edit-item-form" onSubmit={handleSubmit((d) => mutation.mutate(d))}>
            <Field.Root>
              <Field.Label>Name</Field.Label>
              <Input {...register("name", { required: true })} />
            </Field.Root>
          </form>
        </DialogBody>
        <DialogFooter>
          <DialogCloseTrigger asChild><Button variant="ghost">Cancel</Button></DialogCloseTrigger>
          <Button type="submit" form="edit-item-form" loading={mutation.isPending} colorPalette="blue">Save</Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

// ── Provider rate override panel ──────────────────────────────────────────────

const RateOverridePanel = ({
  item,
  providerId,
}: {
  item: ServiceItem
  providerId: string
}) => {
  const queryClient = useQueryClient()
  const [showAudit, setShowAudit] = useState(false)
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      billingIncrementMinutes: item.billingIncrementMinutes ?? 30,
      costPerIncrement: item.costPerIncrement ?? 0,
      recommendedIntervalDays: item.recommendedIntervalDays ?? null as number | null,
    },
  })

  const upsertMutation = useMutation({
    mutationFn: (data: { billingIncrementMinutes: number; costPerIncrement: number; recommendedIntervalDays: number | null }) =>
      ServiceItemProviderRates.upsertRate({
        path: { serviceItemId: item.id, providerId },
        body: {
          billingIncrementMinutes: Number(data.billingIncrementMinutes),
          costPerIncrement: Number(data.costPerIncrement),
          recommendedIntervalDays: data.recommendedIntervalDays ? Number(data.recommendedIntervalDays) : null,
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["serviceItems", item.budgetSubCategoryId] }),
    onError: (err: ApiError) => handleError(err),
  })

  const deleteMutation = useMutation({
    mutationFn: () => ServiceItemProviderRates.deleteRate({ path: { serviceItemId: item.id, providerId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceItems", item.budgetSubCategoryId] })
      reset({ billingIncrementMinutes: 30, costPerIncrement: 0, recommendedIntervalDays: null })
    },
    onError: (err: ApiError) => handleError(err),
  })

  const { data: audit } = useQuery({
    queryKey: ["rateAudit", item.id, providerId],
    queryFn: () => ServiceItemProviderRates.getAudit({ path: { serviceItemId: item.id, providerId } }),
    enabled: showAudit,
  })

  return (
    <Box mt={2} p={3} bg="blue.50" borderRadius="md" fontSize="sm">
      <Text fontWeight="semibold" mb={2} color="blue.700">Provider Rate</Text>
      <form onSubmit={handleSubmit((d) => upsertMutation.mutate({ billingIncrementMinutes: Number(d.billingIncrementMinutes), costPerIncrement: Number(d.costPerIncrement), recommendedIntervalDays: d.recommendedIntervalDays ? Number(d.recommendedIntervalDays) : null }))}>
        <Flex gap={3} align="flex-end" flexWrap="wrap">
          <Field.Root flex={1} minW="140px">
            <Field.Label fontSize="xs">Increment (min)</Field.Label>
            <Input size="sm" type="number" {...register("billingIncrementMinutes")} />
          </Field.Root>
          <Field.Root flex={1} minW="140px">
            <Field.Label fontSize="xs">Cost / increment ($)</Field.Label>
            <Input size="sm" type="number" step="0.01" {...register("costPerIncrement")} />
          </Field.Root>
          <Field.Root flex={1} minW="120px">
            <Field.Label fontSize="xs">Recommended interval (days)</Field.Label>
            <Input size="sm" type="number" placeholder="e.g. 14" {...register("recommendedIntervalDays", { setValueAs: (v) => v === "" ? null : Number(v) })} />
          </Field.Root>
          <Button size="sm" type="submit" colorPalette="blue" loading={upsertMutation.isPending}>
            {item.hasProviderRate ? "Update rate" : "Set rate"}
          </Button>
          {item.hasProviderRate && (
            <IconButton size="sm" variant="ghost" colorPalette="red" aria-label="Remove rate" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              <FiRotateCcw />
            </IconButton>
          )}
        </Flex>
      </form>
      <Button size="xs" variant="ghost" mt={2} onClick={() => setShowAudit(!showAudit)}>
        <FiClock /> {showAudit ? "Hide" : "Show"} rate history
      </Button>
      {showAudit && audit && audit.length > 0 && (
        <Table.Root size="sm" mt={2}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Date</Table.ColumnHeader>
              <Table.ColumnHeader>By</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader>Inc (min)</Table.ColumnHeader>
              <Table.ColumnHeader>$/inc</Table.ColumnHeader>
              <Table.ColumnHeader>Interval (days)</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {(audit as ServiceItemProviderRateAudit[]).map((a, i) => (
              <Table.Row key={i}>
                <Table.Cell>{new Date(a.changedAt).toLocaleDateString()}</Table.Cell>
                <Table.Cell>{a.changedByName ?? "—"}</Table.Cell>
                <Table.Cell><Badge size="sm">{a.changeType}</Badge></Table.Cell>
                <Table.Cell>{a.billingIncrementMinutes}</Table.Cell>
                <Table.Cell>${a.costPerIncrement?.toFixed(2)}</Table.Cell>
                <Table.Cell>{a.recommendedIntervalDays ?? "—"}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Box>
  )
}

// ── Service item row ──────────────────────────────────────────────────────────

const ServiceItemRow = ({
  item,
  providerId,
  isSuperuser,
  onDelete,
}: {
  item: ServiceItem
  providerId: string
  isSuperuser: boolean
  onDelete: () => void
}) => {
  const [expanded, setExpanded] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: () => ServiceCatalog.deleteServiceItem({ path: { serviceItemId: item.id } }),
    onSuccess: onDelete,
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <>
      <Table.Row _hover={{ bg: "gray.50" }}>
        <Table.Cell>
          <Flex align="center" gap={2}>
            <IconButton size="xs" variant="ghost" aria-label="expand" onClick={() => setExpanded(!expanded)}>
              {expanded ? <FiChevronDown /> : <FiChevronRight />}
            </IconButton>
            <Box>
              <Text fontSize="sm" fontWeight="medium">{item.name}</Text>
              {item.isGlobal && <Badge size="xs" colorPalette="purple" ml={1}>Global</Badge>}
              {!item.hasProviderRate && <Badge size="xs" colorPalette="orange" ml={1}>No rate set</Badge>}
            </Box>
          </Flex>
        </Table.Cell>
        <Table.Cell fontSize="sm">
          {item.billingIncrementMinutes != null ? `${item.billingIncrementMinutes} min` : "—"}
        </Table.Cell>
        <Table.Cell fontSize="sm">
          {item.costPerIncrement != null ? `$${item.costPerIncrement.toFixed(2)}` : "—"}
        </Table.Cell>
        <Table.Cell fontSize="sm">
          {item.recommendedIntervalDays != null ? `${item.recommendedIntervalDays}d` : "—"}
        </Table.Cell>
        <Table.Cell textAlign="right">
          {(!item.isGlobal || isSuperuser) && (
            <Flex gap={1} justify="flex-end">
              <EditItemDialog
                item={item}
                trigger={
                  <IconButton size="xs" variant="ghost" aria-label="edit">
                    <FiEdit2 />
                  </IconButton>
                }
              />
              <IconButton
                size="xs"
                variant="ghost"
                colorPalette="red"
                aria-label="delete"
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                <FiTrash2 />
              </IconButton>
            </Flex>
          )}
        </Table.Cell>
      </Table.Row>

      {expanded && (
        <Table.Row>
          <Table.Cell colSpan={4} bg="gray.50" py={3} px={4}>
            <RateOverridePanel item={item} providerId={providerId} />
          </Table.Cell>
        </Table.Row>
      )}
    </>
  )
}

// ── Add service item form ─────────────────────────────────────────────────────

const AddItemForm = ({
  budgetSubCategoryId,
  onAdded,
}: {
  budgetSubCategoryId: string
  onAdded: () => void
}) => {
  const { register, handleSubmit, reset } = useForm<{ name: string }>()

  const mutation = useMutation({
    mutationFn: (data: { name: string }) =>
      ServiceCatalog.createServiceItem({ body: { budgetSubCategoryId, name: data.name } }),
    onSuccess: () => { reset(); onAdded() },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <Box as="form" onSubmit={handleSubmit((d) => mutation.mutate(d))} mt={2}>
      <Flex gap={2} align="flex-end">
        <Field.Root flex={1}>
          <Field.Label fontSize="xs">Item name</Field.Label>
          <Input size="sm" placeholder="e.g. Personal Care — Standard" {...register("name", { required: true })} />
        </Field.Root>
        <Button size="sm" type="submit" colorPalette="green" loading={mutation.isPending}>
          <FiPlus /> Add
        </Button>
      </Flex>
    </Box>
  )
}

// ── BudgetSubCategory panel ───────────────────────────────────────────────────

const SubCategoryPanel = ({
  sub,
  providerId,
  isSuperuser,
  isGlobal,
  onSubDeleted,
}: {
  sub: BudgetSubCategoryPublic
  providerId: string
  isSuperuser: boolean
  isGlobal: boolean
  onSubDeleted: () => void
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(sub.name)
  const queryClient = useQueryClient()

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["serviceItems", sub.id, providerId],
    queryFn: () => ServiceCatalog.listItemsBySubcategory({ path: { subCategoryId: sub.id } }),
  })
  const items = itemsData?.data ?? []

  const updateMutation = useMutation({
    mutationFn: () => ServiceCatalog.updateSubcategory({ path: { subCategoryId: sub.id }, body: { name: editName } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgetSubCategories"] })
      setEditing(false)
    },
    onError: (err: ApiError) => handleError(err),
  })

  const deleteMutation = useMutation({
    mutationFn: () => ServiceCatalog.deleteSubcategory({ path: { subCategoryId: sub.id } }),
    onSuccess: onSubDeleted,
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden" mb={3}>
      <Flex
        px={3} py={2}
        bg="gray.50"
        align="center"
        justify="space-between"
        borderBottomWidth={collapsed ? "0" : "1px"}
        cursor="pointer"
        onClick={() => !editing && setCollapsed(!collapsed)}
      >
        <Flex align="center" gap={2}>
          {collapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
          {editing ? (
            <Flex gap={2} align="center" onClick={(e) => e.stopPropagation()}>
              <Input size="xs" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
              <Button size="xs" colorPalette="blue" loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>Save</Button>
              <Button size="xs" variant="ghost" onClick={() => { setEditing(false); setEditName(sub.name) }}>Cancel</Button>
            </Flex>
          ) : (
            <Text fontWeight="semibold" fontSize="sm">{sub.name}</Text>
          )}
          <Badge size="sm" colorPalette="gray">{items.length} item{items.length !== 1 ? "s" : ""}</Badge>
        </Flex>
        {(!isGlobal || isSuperuser) && (
          <Flex gap={1} onClick={(e) => e.stopPropagation()}>
            <IconButton size="xs" variant="ghost" aria-label="edit sub" onClick={() => setEditing(true)}><FiEdit2 /></IconButton>
            <IconButton size="xs" variant="ghost" colorPalette="red" aria-label="delete sub" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}><FiTrash2 /></IconButton>
          </Flex>
        )}
      </Flex>

      {!collapsed && (
        <Box px={3} py={2}>
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Item</Table.ColumnHeader>
                  <Table.ColumnHeader>Increment</Table.ColumnHeader>
                  <Table.ColumnHeader>Cost/Inc</Table.ColumnHeader>
                  <Table.ColumnHeader>Interval (days)</Table.ColumnHeader>
                  <Table.ColumnHeader />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {items.map((item) => (
                  <ServiceItemRow
                    key={item.id}
                    item={item}
                    providerId={providerId}
                    isSuperuser={isSuperuser}
                    onDelete={() => queryClient.invalidateQueries({ queryKey: ["serviceItems", sub.id] })}
                  />
                ))}
              </Table.Body>
            </Table.Root>
          )}
          {(!isGlobal || isSuperuser) && (
            <AddItemForm
              budgetSubCategoryId={sub.id}
              onAdded={() => queryClient.invalidateQueries({ queryKey: ["serviceItems", sub.id] })}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

// ── BudgetCategory section ────────────────────────────────────────────────────

const BudgetCategorySection = ({
  category,
  providerId,
  isSuperuser,
  isGlobal,
}: {
  category: BudgetCategoryPublic
  providerId: string
  isSuperuser: boolean
  isGlobal: boolean
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const queryClient = useQueryClient()

  const { data: subsData, isLoading } = useQuery({
    queryKey: ["budgetSubCategories", providerId, category.id],
    queryFn: () => ServiceCatalog.listSubcategoriesByProvider({ path: { providerId }, query: { budgetCategoryId: category.id, subCategoryType: "service" } }),
  })
  const subs = subsData?.data ?? []

  return (
    <Box mb={6}>
      <Flex
        align="center"
        justify="space-between"
        mb={3}
        cursor="pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <Flex align="center" gap={2}>
          {collapsed ? <FiChevronRight /> : <FiChevronDown />}
          <Heading size="sm">{category.category}</Heading>
          <Badge colorPalette="blue" size="sm">{subs.length} sub-categor{subs.length !== 1 ? "ies" : "y"}</Badge>
        </Flex>
      </Flex>

      {!collapsed && (
        <>
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            subs.map((sub) => (
              <SubCategoryPanel
                key={sub.id}
                sub={sub}
                providerId={providerId}
                isSuperuser={isSuperuser}
                isGlobal={isGlobal}
                onSubDeleted={() => queryClient.invalidateQueries({ queryKey: ["budgetSubCategories", providerId, category.id] })}
              />
            ))
          )}
        </>
      )}
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ServiceCatalogPage() {
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""
  const providerName = sessionStorage.getItem("selectedProviderName") ?? "Provider"
  const { user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  const [selectedTypeId, setSelectedTypeId] = useState<string>("")

  const { data: typesData } = useQuery({
    queryKey: ["budgetCategoryTypes"],
    queryFn: () => BudgetCategoryTypes.listBudgetCategoryTypes(),
  })
  const types: BudgetCategoryTypePublic[] = typesData?.data ?? []

  const { data: categoriesData, isLoading: loadingCats } = useQuery({
    queryKey: ["budgetCategories", selectedTypeId],
    queryFn: () => BudgetCategorys.readBudgetCategorys(),
    select: (d) => ({
      ...d,
      data: selectedTypeId
        ? d.data.filter((c) => c.budgetCategoryTypeId === selectedTypeId)
        : d.data,
    }),
    enabled: true,
  })
  const categories: BudgetCategoryPublic[] = categoriesData?.data ?? []

  return (
    <Container maxW="full">
      <Box pt={12} mb={6}>
        <Heading size="lg" mb={1}>{providerName} — Service Catalog</Heading>
        <Text color="gray.500" fontSize="sm">
          Manage budget sub-categories and service items. Structure: Category Type → Budget Category → Sub-Category → Items.
        </Text>
      </Box>

      {!providerId && <Text color="red.500">No provider selected.</Text>}

      {/* Budget Category Type filter */}
      <Box mb={6}>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Filter by Category Type</Text>
        <Flex gap={2} flexWrap="wrap">
          <Button
            size="sm"
            variant={selectedTypeId === "" ? "solid" : "outline"}
            colorPalette="gray"
            onClick={() => setSelectedTypeId("")}
          >
            All
          </Button>
          {types.map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant={selectedTypeId === t.id ? "solid" : "outline"}
              colorPalette="blue"
              onClick={() => setSelectedTypeId(t.id)}
            >
              {t.categoryType}
            </Button>
          ))}
        </Flex>
      </Box>

      {loadingCats && <Spinner />}

      {categories.length === 0 && !loadingCats && (
        <Text color="gray.400">No budget categories found{selectedTypeId ? " for this type" : ""}.</Text>
      )}

      {categories.map((cat) => {
        const catType = types.find((t) => t.id === cat.budgetCategoryTypeId)
        return (
        <BudgetCategorySection
          key={cat.id}
          category={cat}
          providerId={providerId}
          isSuperuser={isSuperuser}
          isGlobal={catType?.isGlobal ?? false}
        />
        )
      })}
    </Container>
  )
}
